<template>
  <div
    id="app"
    :style="customStyle"
  >
    <div
      v-if="!origemError"
      style="height: auto;"
    >
      <transition
        mode="out-in"
        name="fade"
        style="height: auto;"
        v-if="!loading && !request"
      >
        <router-view style="height: auto;"/>
      </transition>

      <ErrorHandler
        v-if="request"
        :request="request"
        @click="reload"
      />
    </div>

    <ErrorAlerta
      v-else
      :showBtn="errorConfig.showBtn"
      :texto="errorConfig.texto"
      :titulo="errorConfig.titulo"
    />
  </div>
</template>

<script>
import status from '@/mixins/status'
import ResizeObserver from 'resize-observer-polyfill'
import { mapGetters, mapMutations, mapState } from 'vuex'
import { decodeEmbedData } from '@/utils/utils'
import { ConfiguradorResource, PlataformaBeneficiarioResource } from '@/API'
import ErrorHandler from '@/components/Error/ErrorHandler'
import ErrorAlerta from '@/components/Error/ErrorAlerta'
import { EnumCanalAtendimentoOrigem } from '@/constants/EnumCanalAtendimentoOrigem'

export default {
  components: { ErrorAlerta, ErrorHandler },
  mixins: [status],
  name: 'App',
  data () {
    return {
      ...mapState('SetupApp', ['keepHeightUpdated']),
      origemError: false,
      errorConfig: {
        titulo: 'Não Autorizado',
        texto: 'Você não tem permissão para acessar essa página.',
        showBtn: false
      },
      customHeight: null,
      loading: true,
      resizeTimeout: null
    }
  },
  computed: {
    ...mapState('SetupApp', ['config', 'frameId']),
    ...mapGetters('SetupApp', ['estilo']),
    ...mapState('ErrorStore', ['request']),
    ...mapGetters('Login', ['isLogin', 'user']),
    status () {
      return this.$store.state.status
    },
    customStyle () {
      if (this.customHeight) {
        return {
          'min-height': `${this.customHeight}px`,
          'max-height': `${this.customHeight}px`
        }
      }
      return {
        'height': 'auto'
      }
    }
  },
  methods: {
    ...mapMutations('SetupApp', ['SET_CONFIG', 'SET_ESQUEMA_DE_COR']),
    emmitLoading (isLoading) {
      let data = {
        uid: window.omnisdkmanager ? window.omnisdkmanager.uid : '',
        origin: 'omnichannel',
        client: this.frameId,
        type: 'command',
        command: 'LOADING',
        data: {
          loading: isLoading
        },
        timestamp: Date.now()
      }
      window.parent.postMessage(data, '*')
      this.loading = isLoading
    },
    loadingStart () {
      this.emmitLoading(true)
      this.loading = true
    },
    loadingEnd () {
      this.emmitLoading(false)
      this.loading = false
    },
    async renew (payload) {
      this.loadingStart()
      await this.$store.dispatch('renew', payload.data)
      this.loadingEnd()
      this.$router.push('/')
    },
    async initialize (payload) {
      try {
        if (typeof (payload.data) === 'string') {
          payload.data = JSON.parse(payload.data)
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e)
      }

      window.omnisdkmanager = {
        uid: '',
        isInternalLoading: true,
        client: {
          id: ''
        }
      }

      if (payload.uid) {
        window.omnisdkmanager = {
          uid: payload.uid,
          isInternalLoading: (payload.data && payload.data.json && payload.data.json.isInternalLoading !== undefined) ? payload.data.json.isInternalLoading : true,
          client: {
            id: payload.data.id
          }
        }
      }

      const data = {
        uid: payload.uid ? payload.uid : '',
        origin: 'omnichannel',
        type: 'command',
        client: payload.data.id,
        command: 'CREATED',
        data: {
          ...payload.data.json
        },
        timestamp: new Date().getTime()
      }
      window.parent.postMessage(data, '*')
      const payloadInitialConfig = this.checkInitialConfig(payload.data)
      if (!payloadInitialConfig) {
        return this.$router.push('/404')
      }

      const idFuncionalidade = payload.data.json.idFuncionalidade

      const bundleWeb = payload.data.hostname

      const bundle = decodeEmbedData(payload.data.json.bundle)

      this.customHeight = payload.data.json.height

      if (!bundle) {
        return
      }

      const plataforma = payload.data.json.plataforma
      let canal = payload.data.json.canal
      let numeroChamado = ''
      let protocolo = ''
      let origemEmbed = ''
      switch (payload.data.json.canalAtendimentoOrigem) {
        case EnumCanalAtendimentoOrigem.MOSIA_DESK:
          canal = `${payload.data.json.canalAtendimentoOrigem}`
          numeroChamado = payload.data.json.atendimento.callID
          protocolo = payload.data.json.atendimento.protocolo
          origemEmbed = payload.data.json.origemEmbed
          break
        default:
          break
      }

      if (process.env.NODE_ENV !== 'development' && idFuncionalidade > 9000) {
        const validateOrigem = await ConfiguradorResource.validaOrigemFuncionalidade({
          bundle,
          bundleWeb,
          idFuncionalidade
        })

        if (!validateOrigem) {
          this.origemError = true
          return
        }
      }

      const result = await ConfiguradorResource.getSetupApp({
        bundleWeb,
        bundle,
        instanciaAplicacao: payload.data.json.instanciaAplicacao,
        canalAtendimento: canal
      })

      this.SET_CONFIG({
        config: result,
        bundleWeb,
        bundle: result.aplicacao.bundle,
        frameId: payload.data.id,
        plataforma,
        json: payload.data.json,
        canal,
        numeroChamado,
        protocolo,
        origemEmbed
      })

      if (
        Object.prototype.hasOwnProperty.call(payload.data.json, 'authorization') &&
        payload.data.json.authorization
      ) {
        await this.$store.dispatch('Login/initialize', payload.data)

        let beneficiario = payload.data.json.beneficiario

        if (!Object.prototype.hasOwnProperty.call(payload.data.json, 'beneficiario')) {
          const payloadBeneficiario = await PlataformaBeneficiarioResource.carregar()
          if (payloadBeneficiario.status) {
            beneficiario = payloadBeneficiario
          }
        }

        if (beneficiario) {
          await this.$store.dispatch('Login/setBeneficiario', beneficiario)
        } else {
          await this.$store.dispatch('Login/setToken', null)
        }
      }

      const esquemaCor = payload.data.esquemaCor || payload.data.json.esquemaCor || (this.user || {}).esquemaCor
      await this.aplicarEsquemaCor(esquemaCor, bundle, bundleWeb)

      // Observa a altura do app e informa ao SDK sempre houver alteração
      const resize = new ResizeObserver((entries, observer) => {
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout)
        }
        this.resizeTimeout = setTimeout(() => {
          this.updateHeightIframe(payload.data)
        }, 100)
      })
      resize.observe(document.body)

      // const isAtendente = parseInt(idFuncionalidade) === 63
      // if (
      //   isAtendente &&
      //   Object.prototype.hasOwnProperty.call(payload.data.json, 'atendimento') &&
      //   Object.prototype.hasOwnProperty.call(payload.data.json.atendimento, 'tokenAtendente')
      // ) {
      //   const atendente = ChatApiResource.getAtendente(payload.data.json.atendimento.tokenAtendente)
      // }

      if (
        EnumCanalAtendimentoOrigem.MOSIA_DESK === payload.data.json.canalAtendimentoOrigem &&
        !this.isLogin
      ) {
        this.$router.replace({ path: `/141?redirect=${idFuncionalidade}` })
      } else {
        this.$router.replace({ path: `/${idFuncionalidade}` })
      }

      this.loadingEnd()

      // Detalhe
      if (payload.data.json.idRegistro) {
        return this.$router.push({
          path: `/${payload.data.json.idFuncionalidade}/detalhe`,
          query: {
            idTitulo: payload.data.json.idRegistro,
            codigoTitulo: payload.data.json.idRegistro,
            idNoticia: payload.data.json.idRegistro,
            consulta: { idAgenda: payload.data.json.idRegistro },
            idOcorrencia: payload.data.json.idRegistro
          }
        })
      }
    },
    async receiveMessage (payload) {
      if (payload.origin === 'omnichannelSDK') {
        if (payload.type === 'command') {
          if (payload.command === 'initialize') {
            return this.initialize(payload)
          } else if (payload.command === 'geolocation') {
            await this.setStateGeolocation(payload.data)
          } else {
            const methods = {
              renew: this.renew
            }
            if (Object.prototype.hasOwnProperty.call(methods, payload.command)) {
              try {
                methods[payload.command](payload.data)
              } catch (error) {
                // Ignore error
              }
            }
          }
        }
      }
    },
    async setStateGeolocation (payload) {
      await this.$store.dispatch('RedeCredenciada/setGeolocalizacao', payload)
    },
    async aplicarEsquemaCor (esquemaCor, bundle, bundleWeb) {
      try {
        const res = await ConfiguradorResource.getEsquemaCor({
          bundle,
          bundleWeb,
          esquemaCor
        })

        if (esquemaCor && !res) {
          await this.aplicarEsquemaCor(undefined, bundle, bundleWeb)
        }

        this.SET_ESQUEMA_DE_COR(res)
      } catch (ignored) {}
    },
    checkInitialConfig (payload) {
      // verifica se as propriedades obrigatórias existem
      const propertiesRequired = ['idFuncionalidade']
      try {
        if (typeof (payload) === 'string') {
          payload = JSON.parse(payload)
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e)
      }

      const hasProp = (obj, prop) => {
        return Object.prototype.hasOwnProperty.call(obj, prop)
      }

      let validParams = true

      for (let i = 0; i < propertiesRequired.length; i++) {
        if (!hasProp(payload.json, propertiesRequired[i])) {
          validParams = false
        }
      }

      if (!validParams) {
        return false
      }

      return payload
    },
    updateHeightIframe (data) {
      if (!this.keepHeightUpdated) return

      const milliseconds = new Date().getTime()
      const height = this.$root.$el.clientHeight
      const payload = {
        uid: window.omnisdkmanager ? window.omnisdkmanager.uid : '',
        origin: 'omnichannel',
        client: data.id,
        type: 'command',
        command: 'UPDATE_HEIGHT',
        data: {
          height: height
        },
        timestamp: milliseconds
      }
      window.parent.postMessage(payload, '*')
    },
    fetchData () {
      // Observa mensagens do SDK
      this.$bindEvent(window, 'message', event =>
        this.receiveMessage(event.data)
      )
    },
    reload () {
      this.$router.go()
    }
  },
  created () {
    // if (process.env.NODE_ENV !== 'production') {
    if (this.$route.path.indexOf('playground') !== -1) {
      this.loading = false
      return
    }
    // }
    // fetch the data when the view is created and the data is
    // already being observed
    this.fetchData()
  }
}
</script>
